/*
 * LayoutConfig  type
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { ARROW_POSITION } from "../Toolbar/Toolbar";
import { TLayoutState } from "./LayoutState";

export enum LAYOUT_TYPE {
	COLUMN = "column",
	ROW = "row",
	PANEL = "panel",
	SPLITTER = "splitter",
	TABS = "tabs"
}

export enum LAYOUT_TABS_STYLE {
	EXPAND = "expand",
	BUTTON = "button"
}

export enum LAYOUT_SPLITTER_STYLES {
	LIGHT = "light"
}

export interface ILayoutElementConfigBase {
	id: string;
	type: LAYOUT_TYPE;
}

export interface ILayoutElementConfigExpandable extends ILayoutElementConfigBase {
	size?: number | string;
	isExpandable?: boolean;
	isHideable?: boolean;
	defaultExpanded?: boolean;
	defaultHidden?: boolean;
	renderWhenCollapsed?: boolean;
	renderWhenHidden?: boolean;
}

export interface ILayoutElementConfig_Row extends ILayoutElementConfigExpandable {
	type: LAYOUT_TYPE.ROW;
	children?: TLayoutElementConfig[];
}

export interface ILayoutElementConfig_Column extends ILayoutElementConfigExpandable {
	type: LAYOUT_TYPE.COLUMN;
	children?: TLayoutElementConfig[];
}

export interface ILayoutElementConfig_Panel extends ILayoutElementConfigExpandable {
	type: LAYOUT_TYPE.PANEL;
	arrowPosition?: ARROW_POSITION;
	toolbarComponent?: string;
	secondaryToolbarComponent?: string;
	contentComponent?: string;
	children?: TLayoutElementConfig[];
}

export interface ILayoutElementConfig_Tabs extends ILayoutElementConfigExpandable {
	type: LAYOUT_TYPE.TABS;
	size?: number | string;
	arrowPosition?: ARROW_POSITION;
	styles?: LAYOUT_TABS_STYLE;
	tabs: Array<{
		id: string;
		label?: string;
		icon?: string;
		tooltip?: string;
		contentComponent: string;
		renderWhenHidden?: boolean;
		additionalTabs?: string;
		infoBadge?: string;
	}>;
	defaultTabIndex?: number;
}

export interface ILayoutElementConfig_Splitter extends ILayoutElementConfigBase {
	type: LAYOUT_TYPE.SPLITTER;
	styles?: LAYOUT_SPLITTER_STYLES;
}

export type TLayoutElementConfig =
	| ILayoutElementConfig_Row
	| ILayoutElementConfig_Column
	| ILayoutElementConfig_Panel
	| ILayoutElementConfig_Tabs
	| ILayoutElementConfig_Splitter;

export interface ILayoutConfig {
	root: TLayoutElementConfig;
}

export interface ILayoutElementStateDataBase {
	id: string;
}

export interface ILayoutElementStateDataExpandable extends ILayoutElementStateDataBase {
	size: number;
	isExpanded: boolean;
	isHidden: boolean;
}

export interface ILayoutElementStateData_Row extends ILayoutElementStateDataExpandable {
	children?: TLayoutElementStateData[];
}

export interface ILayoutElementStateData_Column extends ILayoutElementStateDataExpandable {
	children?: TLayoutElementStateData[];
}

export interface ILayoutElementStateData_Panel extends ILayoutElementStateDataExpandable {
	children?: TLayoutElementStateData[];
}

export interface ILayoutElementStateData_Tabs extends ILayoutElementStateDataExpandable {
	size: number;
	currentTabIndex: number;
}

export interface ILayoutElementStateData_Splitter extends ILayoutElementStateDataBase {
	active: boolean;
}

export type TLayoutElementStateData =
	| ILayoutElementStateData_Row
	| ILayoutElementStateData_Column
	| ILayoutElementStateData_Panel
	| ILayoutElementStateData_Tabs
	| ILayoutElementStateData_Splitter;

export interface ILayoutStateData {
	root: TLayoutElementStateData;
}

export type TLayoutRenderComponentFn = (componentName: string) => JSX.Element;
export type TLayoutActivateHandler = (element: TLayoutState) => void;
